/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Editor 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/editor/Editor.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 8. 11.
 */
import * as parent from '@components/form/field/Field';
import * as uploader from '@utils/Uploader';
import { Ajax } from '@utils/Ajax';
import { Dom } from '@utils/Dom';
import { Format } from '@utils/Format';
import { Html } from '@utils/Html';
import { Utils } from '@utils/Utils';
import { Language } from '@utils/Language';
import './Editor.css';
import './Wysiwyg.css';

/**
 * Atwho 객체 구조체를 정의한다.
 */
export interface AtWho {
    /**
     * 트리거 문자
     */
    at: string;

    /**
     * 검색 로직 및 선택 시 사용할 url
     */
    url: string;

    /**
     * 자동 완성 리스트에서 보여지는 탬플릿
     */
    displayTpl: string;

    /**
     * 선택 시 입력창에 삽입될 탬플릿
     */
    insertTpl: string;

    /**
     * 리스트 속성값
     */
    displayAttr: string;

    /**
     * 자동완성 결과 개수 제한(기본값: Infinity)
     */
    limit?: number;
}

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 에디터의 데이터가 변경되었을 때 (10초 간격)
     */
    edit?: (field: Editor, value: { content: string; attachments: string[] }) => void;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 필드값이 없을 경우 보일 placeHolder
     */
    emptyText?: string;

    /**
     * 툴바
     */
    toolbars?: string[];

    /**
     * 업로드 url
     */
    url?: string;

    /**
     * 파일업로드여부
     */
    fileUpload?: boolean;

    /**
     * 이미지업로드여부
     */
    imageUpload?: boolean;

    /**
     * 비디오업로드여부
     */
    videoUpload?: boolean;

    /**
     * atwho 객체
     */
    atwho?: AtWho | AtWho[];

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Editor extends parent.Field {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'editor';

    /**
     * 에디터 랜더링 여부
     */
    editorRendered: boolean = false;

    /**
     * 필드값이 없을 경우 보일 placeHolder
     */
    emptyText: string | null = null;

    /**
     * 툴바
     */
    toolbars: string[] | null = null;

    /**
     * 업로드 url
     */
    url: string | null = null;

    /**
     * 파일업로드여부
     */
    fileUpload: boolean = false;

    /**
     * 이미지업로드여부
     */
    imageUpload: boolean = false;

    /**
     * 비디오업로드여부
     */
    videoUpload: boolean = false;

    /**
     * 텍스트에리어 DOM
     */
    $input: Dom<HTMLTextAreaElement> = null;

    /**
     * 위지윅에디터 객체의 textarea
     */
    $editor: any = null;

    /**
     * 위지윅에디터 객체
     */
    editor: any = null;

    /**
     * 업로더 객체
     */
    uploader: uploader.Uploader = null;

    /**
     * atwho 객체
     */
    atwho: AtWho | AtWho[] = null;

    /**
     * 색상 테마 observer
     */
    colorSchemeObserver: MutationObserver | null = null;

    /**
     * 에디터 색상 코드
     */
    colors = [
        'var(--editor-color-1)',
        'var(--editor-color-2)',
        'var(--editor-color-3)',
        'var(--editor-color-4)',
        'var(--editor-color-5)',
        'var(--editor-color-6)',
        'var(--editor-color-7)',
        'var(--editor-color-8)',
        'var(--editor-color-9)',
        'var(--editor-color-10)',
        'var(--editor-color-11)',
        'var(--editor-color-12)',
        'var(--editor-color-13)',
        'var(--editor-color-14)',
        'var(--editor-color-15)',
        'var(--editor-color-16)',
        'var(--editor-color-17)',
        'var(--editor-color-18)',
        'var(--editor-color-19)',
        'var(--editor-color-20)',
        'var(--editor-color-21)',
        'var(--editor-color-22)',
        'var(--editor-color-23)',
        'var(--editor-color-24)',
        'var(--editor-color-25)',
        'var(--editor-color-26)',
        'var(--editor-color-27)',
        'var(--editor-color-28)',
        'var(--editor-color-29)',
        'REMOVE',
    ];

    /**
     * 에디터 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Editor> = null) {
        super(properties);

        this.emptyText = this.properties.emptyText ?? '';
        this.emptyText = this.emptyText.length == 0 ? null : this.emptyText;
        this.toolbars = this.properties.toolbars ?? null;
        this.url = this.properties.url ?? null;
        this.fileUpload = this.properties.fileUpload ?? true;
        this.imageUpload = this.properties.imageUpload ?? true;
        this.videoUpload = this.properties.videoUpload ?? true;
        this.atwho = this.properties.atwho ?? null;
    }

    /**
     * 텍스트에리어 DOM 을 가져온다.
     *
     * @returns 텍스트에리어 DOM
     */
    $getInput(): Dom<HTMLTextAreaElement> {
        if (this.$input === null) {
            this.$input = Html.create('textarea', {
                name: this.name,
            });

            this.$input.on('edit', async () => {
                const value = await this.getValue();
                this.fireEvent('edit', [this, value]);
            });

            this.$input.on('editorFocus', () => {
                this.onFocus();
            });

            this.$input.on('editorBlur', () => {
                this.onBlur();
            });
        }

        return this.$input;
    }

    /**
    /**
     * 업로드 progress 요소를 가져온다. 없으면 생성하여 iframe body에 추가한다.
     *
     * @param attachmentId - 첨부파일 고유값
     * @returns progress element
     */
    getProgress(attachmentId: string): HTMLProgressElement | null {
        const $root = this.editor.get().$el;
        const $img = this.editor.$(`[data-attachment-id="${attachmentId}"]`, $root).get(0) as HTMLElement | undefined;
        if ($img === undefined) return null;

        const doc = $img.ownerDocument;
        const existing = doc.querySelector<HTMLProgressElement>(`progress[data-attachment-id="${attachmentId}"]`);
        if (existing !== null) return existing;

        const progress = doc.createElement('progress');
        progress.setAttribute('data-attachment-id', attachmentId);
        progress.setAttribute('min', '0');
        progress.setAttribute('max', '100');
        progress.setAttribute('value', '0');
        progress.style.setProperty(
            'position-anchor',
            $img.style.getPropertyValue('anchor-name') || `--${attachmentId}`
        );
        doc.body.appendChild(progress);

        return progress;
    }

    /**
     * 업로드 progress 요소를 제거한다.
     *
     * @param draft_id - 첨부파일 고유값
     */
    removeProgress(draft_id: string, attachment: uploader.Attachment): void {
        const $root = this.editor.get().$el;
        const $img = this.editor.$(`[data-attachment-id]`, $root).get(0) as HTMLElement | undefined;
        const doc = $img?.ownerDocument ?? null;
        if (doc !== null) {
            doc.querySelectorAll(`progress[data-attachment-id="${draft_id}"]`).forEach((el) => el.remove());
        }
        this.editor.$(`img[style*="--${draft_id}"]`, $root).each((_i: number, el: HTMLElement) => {
            el.style.removeProperty('anchor-name');
        });

        this.editor.$(`a[data-attachment-id="${draft_id}"] > small`, $root).each((_i: number, el: HTMLElement) => {
            el.innerText = Format.size(attachment.size);
        });
    }

    /**
     * 업로더를 가져온다.
     *
     * @returns 업로더
     */
    getUploader(): uploader.Uploader {
        if (this.uploader === null) {
            if (this.fileUpload || this.imageUpload || this.videoUpload) {
                this.uploader = new uploader.Uploader({
                    url: this.url,
                    update: async (draft_id: string, attachment: uploader.Attachment, status: uploader.Status) => {
                        if (status.status == 'COMPLETED' || status.status == 'FAILED') {
                            this.removeProgress(draft_id, attachment);
                        }

                        const selector = '*[data-attachment-id="' + draft_id + '"]';
                        const $placeholder = this.editor.$(selector, this.editor.get().$el);
                        if ($placeholder.length == 1) {
                            $placeholder.attr('data-attachment-id', attachment.attachment_id);

                            if (status.status == 'READING') {
                                if ($placeholder.is('img') == true) {
                                    const $progress = this.getProgress(attachment.attachment_id);
                                    const progress = Math.round((status.readed / attachment.size) * 100).toString();
                                    $progress?.style.setProperty('--progress', progress);
                                    $progress?.setAttribute('value', progress);
                                } else if ($placeholder.is('a') == true) {
                                    const $root = this.editor.get().$el;
                                    const $fileLink = this.editor.$(`a[data-attachment-id] > small`, $root);
                                    $fileLink.html(`${Format.size(status.readed)} / ${Format.size(attachment.size)}`);
                                }
                            }

                            if (attachment.status == 'DRAFT' || attachment.status == 'PUBLISHED') {
                                if ($placeholder.is('img') == true) {
                                    $placeholder.replaceWith(this.getImage(attachment));
                                } else if ($placeholder.is('a') == true) {
                                    $placeholder.replaceWith(this.getFileLink(attachment));
                                } else if ($placeholder.is('video') == true) {
                                    $placeholder.replaceWith(this.getVideoPlayer(attachment));
                                }
                            }

                            if (attachment.status == 'LOCAL' && status.status == 'FAILED') {
                                $placeholder.remove();
                            }
                        }
                        const $uploading = this.editor.$('*[data-attachment-id].fr-uploading', this.editor.get().$el);
                        if ($uploading.length == 0) {
                            this.editor.get().edit.on();
                        }
                    },
                });
            }
        }

        return this.uploader;
    }

    /**
     * 이미지 태그를 가져온다.
     *
     * @param file
     * @return html
     */
    getImage(attachment: uploader.Attachment): string {
        const attributes: { [key: string]: string } = {
            src: attachment.view,
            'data-attachment-id': attachment.attachment_id,
            'data-status': attachment.status,
        };

        if (attachment.status != 'LOCAL') {
            attributes.src = attachment.download;
            attributes.download = attachment.name;
            attributes['class'] = 'fr-fic fr-dib';
        } else {
            attributes['class'] = 'fr-uploading fr-fic fr-dib';
        }

        const $img = Html.create('img', attributes);
        $img.setStyleProperty('anchor-name', `--${attachment.attachment_id}`);
        return $img.toHtml() + '<br>';
    }

    /**
     * 파일 다운로드 링크 HTML 을 가져온다.
     *
     * @param file
     * @return html
     */
    getFileLink(file: uploader.Attachment): string {
        const attributes: { [key: string]: string } = {
            src: '',
            'data-attachment-id': file.attachment_id,
            'data-status': file.status,
            'data-module': 'attachment',
            'contenteditable': 'false',
            download: '',
        };

        if (file.status != 'LOCAL') {
            attributes.src = file.download;
            attributes.download = file.name;
            attributes['class'] = 'fr-deletable';
        } else {
            attributes['class'] = 'fr-uploading fr-deletable';
        }

        const $link = Html.create('a', attributes);
        $link.append(Html.create('i', { 'data-type': file.type, 'data-extension': file.extension }, file.extension));
        $link.append(Html.create('span', null, file.name));
        $link.append(Html.create('small', null, Format.size(file.size)));

        return $link.toHtml() + '&nbsp;';
    }

    /**
     * 비디오 플레이어를 가져온다.
     *
     * @param file
     * @param is_wrapper
     * @return html
     */
    getVideoPlayer(file: uploader.Attachment, is_wrapper: boolean = false): string {
        const attributes: { [key: string]: string } = {
            'data-attachment-id': file.attachment_id,
            'data-status': file.status,
            'controls': 'true',
        };

        if (file.status != 'LOCAL') {
            attributes.src = file.view;
            attributes.download = file.name;
            attributes['class'] = 'fr-deletable fr-draggable fr-fvc fr-dvi';
        } else {
            attributes['class'] = 'fr-uploading fr-deletable fr-draggable fr-fvc fr-dvi';
        }

        const $video = Html.create('video', attributes);

        if (is_wrapper === true) {
            const $wrapper = Html.create('span', {
                class: 'fr-video fr-dvb fr-draggable',
                contenteditable: 'false',
                draggable: 'true',
            });
            $wrapper.append($video);
            return $wrapper.toHtml() + '<br>';
        }

        return $video.toHtml() + '<br>';
    }

    /**
     * 파일을 삽입한다.
     *
     * @param file
     */
    insertFile(file: uploader.Attachment): void {
        if (['image', 'svg', 'icon'].includes(file.type) == true) {
            this.editor.get().image.insert(file.view, false, {
                'attachment-id': file.attachment_id,
            });
        } else {
            this.editor.get().html.insert(this.getFileLink(file));
        }
    }

    /**
     * 첨부파일을 제거한다.
     *
     * @param file - 제거할 파일객체
     */
    removeFile(file: uploader.Attachment): void {
        const $file = this.editor.$('*[data-attachment-id="' + file.attachment_id + '"]', this.editor.get().$el);
        $file.remove();
    }

    /**
     * 필드에 포커스를 지정한다.
     */
    async focus(): Promise<void> {
        if (this.isRendered() == false) {
            return;
        }

        if (this.$editor === null) {
            await this.renderEditor();
        }

        const editor = this.$editor.data('froala.editor');
        editor.events.focus();
        editor.selection.setAtEnd(editor.$el.get(0));
        editor.selection.restore();
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     * @param is_update - 에디터 내용을 변경할 지 여부
     */
    async setValue(
        value: { content: string; attachments?: string[] } | string | null,
        is_init: boolean = false,
        is_update: boolean = true
    ): Promise<void> {
        await this.lockValue();

        if (typeof value == 'string') {
            value = { content: value, attachments: [] };
        } else {
            value = { content: value?.content ?? null, attachments: value?.attachments ?? [] };
        }

        if (value.attachments.length > 0) {
            const results = await Ajax.get(this.url, {
                attachment_ids: JSON.stringify(value.attachments),
            });
            if (results.success == true) {
                for (const attachment of results.attachments) {
                    this.getUploader().attachments.set(attachment.id, attachment);
                }
            }
        }

        if (is_init == true || is_update == true) {
            this.$editor.froalaEditor('html.set', value.content ?? '');
            this.syncIframe();
        }

        await this.releaseValue(value, is_init);
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 값
     */
    async getValue(): Promise<{ content: string; attachments: string[] } | null> {
        if (this.isRendered() == false) {
            await this.render(true);
        }

        if (this.$editor.froalaEditor('codeView.isActive') === true) {
            this.$editor.froalaEditor('codeView.toggle');
        }

        const content = this.$editor.froalaEditor('html.get');
        await this.getUploader().get();

        const editor = this.$editor.data('froala.editor');
        const attachments = [];
        editor.$el.find('[data-attachment-id]').each(function () {
            attachments.push(this.getAttribute('data-attachment-id'));
        });

        if (content === '' && attachments.length === 0) {
            return null;
        }

        return { content, attachments };
    }

    /**
     * 필드값이 비어있는지 확인한다.
     *
     * @returns 빈값여부
     */
    async isBlank(): Promise<boolean> {
        const value = await this.getValue();
        return value.content.replace(/<\/?(p|br|span)[^>]*>/gi, '').trim().length == 0 && value.attachments.length == 0;
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        this.$editor.froalaEditor('edit.' + (disabled == true ? 'off' : 'on'));
        return super.setDisabled(disabled);
    }

    /**
     * 내부 콘텐츠 높이를 동기화한다.
     */
    async syncIframe(): Promise<void> {
        if (this.$editor === null) {
            await this.renderEditor();
        }

        this.$editor.froalaEditor('size.syncIframe');
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getContent().append(this.$getInput());

        if (this.maxHeight !== null) {
            this.maxHeight = typeof this.maxHeight == 'number' ? this.maxHeight + 'px' : this.maxHeight;
            this.$getContent().setStyle('max-height', this.maxHeight);
        }

        if (this.minHeight !== null) {
            this.minHeight = typeof this.minHeight == 'number' ? this.minHeight + 'px' : this.minHeight;
            this.$getContent().setStyle('min-height', this.minHeight);
        }
    }

    /**
     * 에디터를 렌더링한다.
     */
    async renderEditor(): Promise<void> {
        // 이미 렌더링되었거나, DOM에 연결되지 않은 경우 스킵
        if (this.editorRendered === true || !this.$getInput().getEl().isConnected) {
            return;
        }

        if ((await Utils.loadJs(this.getViewport().getDir() + '/assets/froalaEditor/froalaEditor.js')) == true) {
            const FroalaEditor = (window as any).FroalaEditor;

            const $ = FroalaEditor.init();
            const translation = await Language.get('editor', null, ['/froalaEditor/languages']).getRaw();
            if (typeof translation == 'object') {
                $.FE.LANGUAGE['en'] = {
                    translation: translation,
                };
            }
            this.editor = new FroalaEditor(this.$getInput().getEl(), {
                language: 'en',
                toolbars: this.toolbars,
                colorsText: this.colors,
                colorsBackground: this.colors,
                colorsStep: 10,
                tableColors: this.colors,
                tableColorsStep: 10,
                fileUpload: this.fileUpload,
                imageUpload: this.imageUpload,
                videoUpload: this.videoUpload,
                atwho: this.atwho,
                scrollableContainer:
                    'aui-component[data-component="' +
                    this.id +
                    '"] > div[data-role="container"] > div[data-role="content"]',
                path: this.getViewport().getDir() + '/assets/froalaEditor',
                iframeStyleFiles: [
                    'https://cdn.jsdelivr.net/gh/sun-typeface/SUIT@2/fonts/static/woff2/SUIT.css',
                    this.getViewport().getDir() + '/Aui.css',
                ],
            });
            this.$editor = await this.editor.render((editor: any, $editor: any) => {
                $editor.on('froalaEditor.contentChanged', (e: any) => {
                    Html.el(e.currentTarget).trigger('edit');
                });
                $editor.on('froalaEditor.focus', (e: any) => {
                    Html.el(e.currentTarget).trigger('editorFocus');
                });
                $editor.on('froalaEditor.blur', (e: any) => {
                    Html.el(e.currentTarget).trigger('editorBlur');
                });
                $editor.on('froalaEditor.image.beforePasteUpload', (_e: any, _editor: any, img: HTMLImageElement) => {
                    img.remove();
                    return this.imageUpload;
                });
                $editor.on('froalaEditor.image.beforeUpload', (_e: any, editor: any, files: FileList) => {
                    if (this.imageUpload == false || files.length == 0 || this.url === null) {
                        editor.popups.hideAll();
                        return false;
                    }

                    editor.edit.off();
                    editor.events.focus(true);
                    editor.selection.restore();

                    const imageAttachments = this.getUploader().add(files);
                    for (const attachment of imageAttachments) {
                        const placeholder = this.getImage(attachment);
                        editor.html.insert(placeholder);
                    }
                    this.getUploader().start();

                    editor.placeholder.refresh();
                    editor.popups.hideAll();

                    return false;
                });
                $editor.on('froalaEditor.file.beforeUpload', (_e: any, editor: any, files: FileList) => {
                    if (this.fileUpload == false || files.length == 0 || this.url === null) {
                        editor.popups.hideAll();
                        return false;
                    }

                    editor.edit.off();
                    editor.events.focus(true);
                    editor.selection.restore();

                    const fileAttachments = this.getUploader().add(files);
                    for (const attachment of fileAttachments) {
                        const placeholder = this.getFileLink(attachment);
                        editor.html.insert(placeholder);
                    }
                    this.getUploader().start();

                    editor.placeholder.refresh();
                    editor.popups.hideAll();

                    return false;
                });
                $editor.on('froalaEditor.video.beforeUpload', (_e: any, editor: any, files: FileList) => {
                    if (this.videoUpload == false || files.length == 0 || this.url === null) {
                        editor.popups.hideAll();
                        return false;
                    }

                    editor.edit.off();
                    editor.events.focus(true);
                    editor.selection.restore();

                    const videoAttachments = this.getUploader().add(files);
                    for (const attachment of videoAttachments) {
                        const placeholder = this.getVideoPlayer(attachment, true);
                        editor.html.insert(placeholder);
                    }
                    this.getUploader().start();

                    editor.placeholder.refresh();
                    editor.popups.hideAll();

                    return false;
                });
            });

            let sticky = '0px';
            let parent = this.getParent();
            while (parent !== null) {
                if (parent.getParent() !== null) {
                    sticky = parent.$getContent()?.getStyle('padding-top') ?? '0px';
                    break;
                }

                parent = parent.getParent();
            }

            this.$getContent().setStyleProperty('--im-wysiwyg-toolbar-sticky-height', sticky);

            this.editorRendered = true;
        }
    }

    /**
     * 색상 테마 observer를 초기화한다.
     */
    initColorSchemeObserver(): void {
        if (this.colorSchemeObserver !== null) {
            return;
        }

        const htmlElement = document.documentElement;

        this.colorSchemeObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes') {
                    const attributeName = mutation.attributeName;
                    if (attributeName === 'data-color-scheme' || attributeName === 'data-prefers-color-scheme') {
                        const colorScheme = htmlElement.getAttribute('data-color-scheme');
                        const prefersColorScheme = htmlElement.getAttribute('data-prefers-color-scheme');

                        this.onDarkModeChange(
                            colorScheme === 'dark' || (colorScheme == 'auto' && prefersColorScheme === 'dark')
                        );
                    }
                }
            });
        });

        this.colorSchemeObserver.observe(htmlElement, {
            attributes: true,
            attributeFilter: ['data-color-scheme', 'data-prefers-color-scheme'],
        });

        // 초기 상태 확인
        const colorScheme = htmlElement.getAttribute('data-color-scheme');
        const prefersColorScheme = htmlElement.getAttribute('data-prefers-color-scheme');
        this.onDarkModeChange(colorScheme === 'dark' || (colorScheme == 'auto' && prefersColorScheme === 'dark'));
    }

    /**
     * 다크모드 변경 시 처리
     *
     * @param isDark - 다크모드 여부
     */
    onDarkModeChange(isDark: boolean): void {
        Html.all('iframe[class=fr-iframe]').forEach(($iframe) => {
            const doc = ($iframe.getEl() as HTMLIFrameElement).contentDocument;
            doc.documentElement.setAttribute('data-color-scheme', isDark ? 'dark' : 'auto');
        });
    }

    /**
     * 색상 테마 observer를 정리한다.
     */
    destroyColorSchemeObserver(): void {
        if (this.colorSchemeObserver !== null) {
            this.colorSchemeObserver.disconnect();
            this.colorSchemeObserver = null;
        }
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        await this.renderEditor();
        await super.renderState();

        this.initColorSchemeObserver();
    }

    /**
     * 컴포넌트 DOM 이 연결되었을 때 이벤트를 처리한다.
     */
    async onConnect(): Promise<void> {
        /**
         * 랜더링이 되어 있는 상태인데, DOM 이 다시 연결된 경우 에디터를 재랜더링한다.
         */
        if (this.isRendered() == true) {
            this.editorRendered = false;
            await this.renderEditor();
        }

        await super.onConnect();
    }

    /**
     * 컴포넌트 DOM 이 연결해제되었을 때 이벤트를 처리한다.
     */
    async onDisconnect(): Promise<void> {
        if (this.isRendered() == true) {
            this.editor.$editor.froalaEditor('destroy');
        }

        this.destroyColorSchemeObserver();
        await super.onDisconnect();
    }
}
